import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { AlertTriangle, Package, ShoppingCart, Factory, FileCheck, PackageX } from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/sales/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div data-testid="loading-indicator">Loading dashboard...</div>;
  }

  return (
    <div data-testid="dashboard-page">
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '24px' }}>
        Dashboard Overview
      </h1>

      <div className="grid grid-cols-4" style={{ marginBottom: '32px' }}>
        <div className="stat-card" data-testid="stat-total-orders">
          <div className="flex items-center justify-between">
            <div>
              <div className="stat-value">{stats?.total_orders || 0}</div>
              <div className="stat-label">Total Orders</div>
            </div>
            <ShoppingCart size={32} color="var(--text-tertiary)" />
          </div>
        </div>

        <div className="stat-card" data-testid="stat-pending-orders">
          <div className="flex items-center justify-between">
            <div>
              <div className="stat-value">{stats?.pending_orders || 0}</div>
              <div className="stat-label">Pending Orders</div>
            </div>
            <AlertTriangle size={32} color="#FBBF24" />
          </div>
        </div>

        <div className="stat-card" data-testid="stat-production">
          <div className="flex items-center justify-between">
            <div>
              <div className="stat-value">{stats?.production_in_progress || 0}</div>
              <div className="stat-label">In Production</div>
            </div>
            <Factory size={32} color="var(--text-tertiary)" />
          </div>
        </div>

        <div className="stat-card" data-testid="stat-low-stock">
          <div className="flex items-center justify-between">
            <div>
              <div className="stat-value">{stats?.low_stock_count || 0}</div>
              <div className="stat-label">Low Stock Items</div>
            </div>
            <PackageX size={32} color="var(--text-tertiary)" />
          </div>
        </div>
      </div>

      {stats?.low_stock_count > 0 && (
        <div className="card" style={{ marginBottom: '24px' }} data-testid="low-stock-alert">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} color="#FBBF24" />
              <h2 className="card-title">Low Stock Alerts ({stats.low_stock_count})</h2>
            </div>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Name</th>
                  <th className="text-right">Current Qty</th>
                  <th className="text-right">Min Level</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {stats.low_stock_items?.map((item) => (
                  <tr key={item.id} data-testid={`low-stock-item-${item.sku}`}>
                    <td className="font-mono">{item.sku}</td>
                    <td>{item.name}</td>
                    <td className="text-right tabular-nums">{item.quantity}</td>
                    <td className="text-right tabular-nums">{item.min_stock_level}</td>
                    <td>{item.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" data-testid="quick-actions">
        <div className="card-header">
          <h2 className="card-title">Quick Actions</h2>
        </div>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          <a href="/sales-orders" className="btn btn-primary" data-testid="create-order-btn">
            <ShoppingCart size={16} />
            Create Sales Order
          </a>
          <a href="/inventory" className="btn btn-secondary" data-testid="manage-inventory-btn">
            <Package size={16} />
            Manage Inventory
          </a>
          <a href="/production" className="btn btn-secondary" data-testid="production-btn">
            <Factory size={16} />
            Production
          </a>
          <a href="/coa" className="btn btn-secondary" data-testid="coa-btn">
            <FileCheck size={16} />
            Certificate of Analysis
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
